import type { TFunction } from 'i18next';
import type { AutoWithdrawalNetworkCode } from '@shared/api/contracts';
import {
  detectAddressFamily,
  type CryptoAddressValidationErrorCode,
} from '@shared/lib/validate-crypto-address';

export interface AddressFeedback {
  message: string;
  suggestedNetwork: AutoWithdrawalNetworkCode | null;
}

const SUGGESTED_NETWORK: Partial<Record<NonNullable<ReturnType<typeof detectAddressFamily>>, AutoWithdrawalNetworkCode>> = {
  TRON: 'TRC20',
  BTC: 'BTC',
  LTC: 'LTC',
};

export const describeAddressFeedback = (
  address: string,
  network: string,
  errorCode: CryptoAddressValidationErrorCode | undefined,
  t: TFunction,
): AddressFeedback => {
  const selected = network.trim();
  const family = detectAddressFamily(address);
  const suggestedNetwork = family ? SUGGESTED_NETWORK[family] ?? null : null;
  const suggestion = suggestedNetwork && suggestedNetwork !== selected.replace(/-/g, '').toUpperCase()
    ? suggestedNetwork
    : null;

  if (errorCode === 'NETWORK_MISMATCH' && family === 'TRON') {
    return { message: t('withdrawal.validation.addressMismatchTron', { network: selected }), suggestedNetwork: suggestion };
  }
  if (errorCode === 'NETWORK_MISMATCH' && family === 'EVM') {
    return { message: t('withdrawal.validation.addressMismatchEvm', { network: selected }), suggestedNetwork: null };
  }
  if (errorCode === 'NETWORK_MISMATCH' && family === 'BTC') {
    return { message: t('withdrawal.validation.addressMismatchBtc', { network: selected }), suggestedNetwork: suggestion };
  }
  if (errorCode === 'NETWORK_MISMATCH' && family === 'LTC') {
    return { message: t('withdrawal.validation.addressMismatchLtc', { network: selected }), suggestedNetwork: suggestion };
  }
  if (errorCode === 'INVALID_ADDRESS_CHECKSUM') {
    return { message: t('withdrawal.validation.invalidAddressChecksum'), suggestedNetwork: null };
  }

  return {
    message: t('withdrawal.validation.addressInvalidForNetwork', { network: selected || network }),
    suggestedNetwork: null,
  };
};
